import { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Input, Tabs, Typography, Space, Button, message, Modal, Empty, Badge, Spin, Tooltip, Segmented, Table, Tag,
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, AppstoreOutlined, BarsOutlined, ToolOutlined,
  SettingOutlined, ApiOutlined, CloudOutlined, LockOutlined,
  DownloadOutlined, DeleteOutlined, PauseCircleOutlined, PlayCircleOutlined,
} from '@ant-design/icons';
import usePluginStore from '../plugin-manager';
import useAuthStore from '../../stores/authStore';
import type { Plugin } from '../plugin-types';
import PluginCard from '../components/PluginCard';
import * as pluginApi from '../plugin-api';

const { Title, Text } = Typography;

interface MarketPlugin {
  id: string;
  name: string;
  version: string;
  description: string;
  author: string;
  category: string;
  status: string;
  installed: boolean;
}

const categoryTabs = [
  { key: 'all', label: '全部', icon: <AppstoreOutlined /> },
  { key: 'iot', label: '设备', icon: <ToolOutlined /> },
  { key: 'data', label: '数据', icon: <ApiOutlined /> },
  { key: 'scene', label: '场景', icon: <SettingOutlined /> },
  { key: 'system', label: '系统', icon: <CloudOutlined /> },
];
const CATEGORY_LABELS: Record<string, string> = { iot: '设备', data: '数据', scene: '场景', system: '系统' };

export default function PluginMarket() {
  const [searchText, setSearchText] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  // 2026-10-10（用户要求）：卡片/列表切换（localStorage 持久）+ 列表分页管理（参考可视化大屏列表）
  const [viewMode, setViewMode] = useState<'card' | 'list'>(() => (localStorage.getItem('pluginMarketView') as 'card' | 'list') || 'card');
  const [marketPlugins, setMarketPlugins] = useState<MarketPlugin[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedPlugin, setSelectedPlugin] = useState<MarketPlugin | null>(null);

  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'tenant_admin';

  const {
    plugins,
    loadingPlugins,
    errorPlugins,
    installPlugin,
    uninstallPlugin,
    enablePlugin,
    disablePlugin,
    loadPlugin,
  } = usePluginStore();

  const fetchMarketData = useCallback(async () => {
    setLoading(true);
    try {
      const [marketItems, installedItems] = await Promise.all([
        pluginApi.getMarketList(),
        pluginApi.getInstalledPlugins(),
      ]);

      const installedIds = new Set(installedItems.map((p) => p.pluginId));
      const enabledIds = new Set(
        installedItems.filter((p) => p.status === 'enabled').map((p) => p.pluginId)
      );

      const merged = marketItems.map((item) => ({
        ...item,
        installed: installedIds.has(item.id),
        status: enabledIds.has(item.id) ? 'enabled' : installedIds.has(item.id) ? 'installed' : item.status,
      }));

      setMarketPlugins(merged);

      for (const serverPlugin of installedItems) {
        const exists = plugins.find((p) => p.id === serverPlugin.pluginId);
        if (!exists) {
          const metadata = {
            id: serverPlugin.pluginId,
            name: serverPlugin.name,
            version: serverPlugin.version,
            description: '',
            author: 'DTwince',
            entry: 'default',
            url: '',
          };
          await installPlugin(metadata);
        }
      }
    } catch (error) {
      message.error('获取市场数据失败');
    } finally {
      setLoading(false);
    }
  }, [plugins, installPlugin]);

  useEffect(() => {
    fetchMarketData();
  }, [fetchMarketData]);

  const handleInstall = async (plugin: MarketPlugin) => {
    try {
      await pluginApi.installPlugin(plugin.id);
      await pluginApi.enablePlugin(plugin.id);
      const metadata = {
        id: plugin.id,
        name: plugin.name,
        version: plugin.version,
        description: plugin.description,
        author: plugin.author,
        entry: 'default',
        url: '',
      };
      await installPlugin(metadata);
      message.success(`插件 ${plugin.name} 安装并启用成功`);
      fetchMarketData();
    } catch (error) {
      message.error(`安装失败: ${(error as Error).message}`);
    }
  };

  const handleUninstall = (pluginId: string) => {
    Modal.confirm({
      title: '确认卸载',
      content: '确定要卸载此插件吗？卸载后相关功能将不可用。',
      onOk: async () => {
        try {
          const status = await pluginApi.getPluginStatus(pluginId);
          if (status.status === 'enabled') {
            await pluginApi.disablePlugin(pluginId);
          }
          await pluginApi.uninstallPlugin(pluginId);
          uninstallPlugin(pluginId);
          message.success('插件已卸载');
          fetchMarketData();
        } catch (error) {
          message.error(`卸载失败: ${(error as Error).message}`);
        }
      },
    });
  };

  const handleToggle = async (pluginId: string, enabled: boolean) => {
    try {
      if (enabled) {
        await pluginApi.enablePlugin(pluginId);
        enablePlugin(pluginId);
        await loadPlugin(pluginId);
        message.success('插件已启用');
      } else {
        await pluginApi.disablePlugin(pluginId);
        disablePlugin(pluginId);
        message.success('插件已禁用');
      }
      fetchMarketData();
    } catch (error) {
      message.error(`操作失败: ${(error as Error).message}`);
    }
  };

  const getLocalPluginStatus = (pluginId: string) => {
    const marketItem = marketPlugins.find((p) => p.id === pluginId);
    return marketItem?.status || 'available';
  };

  const isLocalPluginEnabled = (pluginId: string) => {
    const marketItem = marketPlugins.find((p) => p.id === pluginId);
    return marketItem?.status === 'enabled';
  };

  const getPluginAsMarketItem = (p: Plugin): MarketPlugin => ({
    id: p.id,
    name: p.name,
    version: p.version,
    description: p.description || '',
    author: p.author || '',
    category: 'iot',
    status: getLocalPluginStatus(p.id),
    installed: true,
  });

  const filterPlugins = (pluginList: MarketPlugin[]) => {
    let filtered = pluginList;
    if (activeCategory !== 'all') {
      filtered = filtered.filter((p) => p.category === activeCategory);
    }
    if (searchText) {
      const kw = searchText.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(kw) ||
          p.description.toLowerCase().includes(kw) ||
          p.id.toLowerCase().includes(kw)
      );
    }
    return filtered;
  };

  const installedMarketPlugins = marketPlugins.filter((p) => p.installed);
  const notInstalledMarketPlugins = marketPlugins.filter((p) => !p.installed);

  const renderStatusTag = (p: MarketPlugin) => {
    if (errorPlugins.find((e) => e.id === p.id)) return <Tag color="error">加载失败</Tag>;
    if (loadingPlugins.includes(p.id)) return <Tag color="processing">加载中</Tag>;
    if (p.installed) return isLocalPluginEnabled(p.id) ? <Tag color="success">已启用</Tag> : <Tag color="warning">已安装</Tag>;
    return <Tag>可安装</Tag>;
  };

  const renderRowActions = (p: MarketPlugin) => {
    const loading = loadingPlugins.includes(p.id);
    return (
      <Space size={4} wrap>
        {!p.installed && isAdmin && (
          <Button size="small" type="link" icon={<DownloadOutlined />} loading={loading} onClick={() => handleInstall(p)}>安装</Button>
        )}
        {!p.installed && !isAdmin && (
          <Button size="small" type="link" icon={<LockOutlined />} disabled>仅管理员</Button>
        )}
        {p.installed && isAdmin && (
          <Button size="small" type="link" danger icon={<DeleteOutlined />} onClick={() => handleUninstall(p.id)}>卸载</Button>
        )}
        {p.installed && isAdmin && (isLocalPluginEnabled(p.id) ? (
          <Button size="small" type="link" icon={<PauseCircleOutlined />} onClick={() => handleToggle(p.id, false)}>禁用</Button>
        ) : (
          <Button size="small" type="link" icon={<PlayCircleOutlined />} onClick={() => handleToggle(p.id, true)}>启用</Button>
        ))}
        {p.installed && (
          <Button size="small" type="link" icon={<ReloadOutlined />} onClick={() => loadPlugin(p.id)}>重新加载</Button>
        )}
      </Space>
    );
  };

  const renderPluginSection = (
    title: string,
    pluginList: MarketPlugin[],
    isInstalled: boolean,
    icon?: React.ReactNode
  ) => {
    const filtered = filterPlugins(pluginList);
    return (
      <div style={{ marginBottom: 32 }}>
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          {icon}
          <Title level={4} style={{ margin: 0 }}>{title}</Title>
          <Badge count={filtered.length} style={{ backgroundColor: '#1890ff' }} />
        </div>
        {viewMode === 'list' ? (
          <Table
            rowKey="id"
            size="middle"
            dataSource={filtered}
            pagination={{ pageSize: 10, showSizeChanger: false, showTotal: (t) => `共 ${t} 条` }}
            columns={[
              {
                title: '名称', dataIndex: 'name',
                render: (v: string, p: MarketPlugin) => (
                  <Space size={6}><span style={{ fontWeight: 600 }}>{v}</span>{renderStatusTag(p)}</Space>
                ),
              },
              { title: '版本', dataIndex: 'version', width: 100, render: (v: string) => <Tag color="blue">{v}</Tag> },
              { title: '分类', dataIndex: 'category', width: 90, render: (v: string) => <Tag>{CATEGORY_LABELS[v] || v || '—'}</Tag> },
              { title: '作者', dataIndex: 'author', width: 110, render: (v: string) => v || '—' },
              { title: '描述', dataIndex: 'description', ellipsis: true, render: (v: string) => <Text type="secondary" style={{ fontSize: 12 }}>{v || '—'}</Text> },
              { title: '操作', width: 260, render: (_: unknown, p: MarketPlugin) => renderRowActions(p) },
            ]}
          />
        ) : (
          <Row gutter={[16, 16]}>
            {filtered.map((plugin) => (
              <Col xs={24} sm={12} lg={8} xl={6} key={plugin.id}>
                <PluginCard
                  plugin={plugin as any}
                  isInstalled={isInstalled}
                  isLoading={loadingPlugins.includes(plugin.id)}
                  error={errorPlugins.find((e) => e.id === plugin.id)?.error}
                  isEnabled={isInstalled ? isLocalPluginEnabled(plugin.id) : true}
                  isAdmin={isAdmin}
                  onInstall={() => handleInstall(plugin)}
                  onUninstall={() => handleUninstall(plugin.id)}
                  onToggle={handleToggle}
                  onReload={(id) => loadPlugin(id)}
                  onConfig={(p) => {
                    setSelectedPlugin(getPluginAsMarketItem(p as Plugin));
                    setDetailModalVisible(true);
                  }}
                />
              </Col>
            ))}
          </Row>
        )}
        {filtered.length === 0 && <Empty description="暂无插件" style={{ padding: '40px 0' }} />}
      </div>
    );
  };

  return (
    <div style={{ padding: 24, minHeight: '100vh' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>插件市场</Title>
        <Text type="secondary">管理和安装插件以扩展平台功能</Text>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Space>
          <Input
            placeholder="搜索插件..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<SearchOutlined />}
            style={{ width: 400 }}
            size="large"
          />
          <Button
            icon={<ReloadOutlined />}
            size="large"
            onClick={fetchMarketData}
            loading={loading}
          >
            刷新
          </Button>
          <Segmented
            size="large"
            value={viewMode}
            onChange={(v) => { const m = v as 'card' | 'list'; setViewMode(m); localStorage.setItem('pluginMarketView', m); }}
            options={[
              { value: 'card', icon: <AppstoreOutlined />, label: '卡片' },
              { value: 'list', icon: <BarsOutlined />, label: '列表' },
            ]}
            data-testid="plugin-view-toggle"
          />
        </Space>
      </div>

      <Tabs
        activeKey={activeCategory}
        onChange={setActiveCategory}
        items={categoryTabs.map((tab) => ({
          key: tab.key,
          label: (
            <span>
              {tab.icon}
              <span style={{ marginLeft: 4 }}>{tab.label}</span>
            </span>
          ),
        }))}
        style={{ marginBottom: 24 }}
      />

      <Spin spinning={loading}>
        {installedMarketPlugins.length > 0 &&
          renderPluginSection('已安装', installedMarketPlugins, true)}

        {renderPluginSection('可用插件', notInstalledMarketPlugins, false)}
      </Spin>

      <Modal
        title="插件详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={600}
      >
        {selectedPlugin && (
          <Space orientation="vertical" style={{ width: '100%' }}>
            <div><Text strong>插件ID:</Text> {selectedPlugin.id}</div>
            <div><Text strong>名称:</Text> {selectedPlugin.name}</div>
            <div><Text strong>版本:</Text> {selectedPlugin.version}</div>
            <div><Text strong>描述:</Text> {selectedPlugin.description}</div>
            <div><Text strong>作者:</Text> {selectedPlugin.author}</div>
            <div>
              <Text strong>状态:</Text>{' '}
              <Badge
                status={selectedPlugin.installed ? 'success' : 'default'}
                text={selectedPlugin.installed ? '已安装' : '可用'}
              />
            </div>
          </Space>
        )}
      </Modal>
    </div>
  );
}
